// Interview questions for the conversation wizard
import type { Formality, Confidence, CallToAction } from '../types';

export interface InterviewQuestion {
  id: string;
  question: string;
  helpText?: string;
  examples?: string[];
  field: string; // Which persona field this maps to
  type: 'text' | 'textarea' | 'select' | 'multiselect';
  options?: string[];
  required?: boolean;
  followUp?: (answer: string) => string | null;
}

export const INTERVIEW_QUESTIONS: InterviewQuestion[] = [
  {
    id: 'intro',
    question:
      "Hi! I'm here to help you create a new persona. Let's start with the basics. What would you like to name this persona?",
    helpText: 'Choose a memorable name that reflects the purpose of this persona',
    examples: ['Thought Leader', 'Tech Educator', 'Brand Voice', 'Community Builder'],
    field: 'name',
    type: 'text',
    required: true,
  },
  {
    id: 'tagline',
    question:
      'Great name! Now, what\'s a short tagline that captures what this persona is about? Think of it as their "elevator pitch."',
    helpText: 'A brief statement that captures the essence of this persona',
    examples: [
      'Shaping the future of AI',
      'Making tech accessible to everyone',
      'Building communities that last',
    ],
    field: 'tagline',
    type: 'text',
    required: false,
  },
  {
    id: 'audience',
    question: 'Who does this persona speak to? Describe your target audience.',
    helpText: 'Be specific about who you want to reach',
    examples: [
      'Health-curious women aged 20-35',
      'Tech professionals seeking career growth',
      'Entrepreneurs building their first startup',
    ],
    field: 'worldview.audience',
    type: 'text',
    required: true,
  },
  {
    id: 'traits',
    question:
      'What personality traits best describe how this persona comes across? Select the ones that fit best.',
    helpText: 'Choose 3-5 traits that define the personality',
    field: 'voice.traits',
    type: 'multiselect',
    options: [
      'Authoritative',
      'Approachable',
      'Analytical',
      'Creative',
      'Empathetic',
      'Direct',
      'Humorous',
      'Professional',
      'Warm',
      'Visionary',
      'Practical',
      'Inspiring',
      'Witty',
      'Patient',
      'Bold',
    ],
    required: true,
  },
  {
    id: 'formality',
    question:
      'How formal is this persona? Would they use proper business language or keep things casual?',
    helpText: 'This affects word choice and sentence structure',
    field: 'decisions.formality',
    type: 'select',
    options: ['Formal', 'Professional', 'Conversational', 'Casual', 'Like texting a friend'],
    required: true,
  },
  {
    id: 'confidence',
    question: 'How certain does this persona sound when sharing ideas?',
    helpText: 'This affects how claims are presented',
    field: 'decisions.confidence',
    type: 'select',
    options: [
      'Still figuring it out',
      'Thinking out loud',
      'Grounded in experience',
      'Sure of themselves',
      'Definitive authority',
    ],
    required: true,
  },
  {
    id: 'callToAction',
    question: 'Does this persona typically ask the audience to do something?',
    helpText: 'How strongly does the persona push for action?',
    field: 'decisions.callToAction',
    type: 'select',
    options: [
      'No, just sharing',
      'Opens conversation',
      'Gentle invitation',
      'Clear recommendation',
      'Strong push',
    ],
    required: true,
  },
  {
    id: 'voice-description',
    question:
      'How would you describe the way this persona communicates? What makes their voice distinctive?',
    helpText: 'Think about their communication patterns and preferences',
    examples: [
      'Uses data to support bold claims',
      'Asks thought-provoking questions',
      'Tells stories to illustrate points',
      'Gets straight to the point',
    ],
    field: 'voice.description',
    type: 'textarea',
    required: true,
  },
  {
    id: 'test',
    question:
      'Perfect! I think I have a good sense of your persona now. Would you like to test it by seeing how it would write about a specific topic?',
    helpText: 'Enter a topic to generate sample content',
    examples: ['The future of remote work', 'Why authenticity matters', 'Building in public'],
    field: '_test',
    type: 'text',
    required: false,
    followUp: (answer) =>
      answer
        ? `Here's how your persona might write about "${answer}". Does this capture the voice you had in mind?`
        : null,
  },
];

// Convert formality select answer to decision value
export function formalitySelectToValue(answer: string): Formality {
  const mapping: Record<string, Formality> = {
    Formal: 'formal',
    Professional: 'professional',
    Conversational: 'conversational',
    Casual: 'casual',
    'Like texting a friend': 'colloquial',
  };
  return mapping[answer] || 'conversational';
}

// Convert confidence select answer to decision value
export function confidenceSelectToValue(answer: string): Confidence {
  const mapping: Record<string, Confidence> = {
    'Still figuring it out': 'tentative',
    'Thinking out loud': 'exploratory',
    'Grounded in experience': 'steady',
    'Sure of themselves': 'confident',
    'Definitive authority': 'authoritative',
  };
  return mapping[answer] || 'steady';
}

// Convert callToAction select answer to decision value
export function callToActionSelectToValue(answer: string): CallToAction {
  const mapping: Record<string, CallToAction> = {
    'No, just sharing': 'none',
    'Opens conversation': 'open',
    'Gentle invitation': 'invite',
    'Clear recommendation': 'recommend',
    'Strong push': 'urge',
  };
  return mapping[answer] || 'open';
}
